import { ref, onUnmounted, provide, h, inject, computed } from 'vue';
import { formSubscriptionItems, createForm, fieldSubscriptionItems } from 'final-form';

var getChildren = function getChildren(children) {
  return Array.isArray(children) ? children : [children];
};

var composeValidators = function composeValidators(validators) {
  for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    args[_key - 1] = arguments[_key];
  }

  return (// eslint-disable-next-line unicorn/no-array-reduce
    validators.reduce(function (error, validator) {
      return error || validator.apply(void 0, args);
    }, undefined)
  );
};

var composeFormValidators = function composeFormValidators(validators) {
  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return composeValidators.apply(void 0, [validators].concat(args));
  };
};
var composeFieldValidators = function composeFieldValidators(validators) {
  return function () {
    return function () {
      for (var _len3 = arguments.length, args = new Array(_len3), _key3 = 0; _key3 < _len3; _key3++) {
        args[_key3] = arguments[_key3];
      }

      return composeValidators.apply(void 0, [validators].concat(args));
    };
  };
};
var makeSubscriptionObject = function makeSubscriptionObject(subscriptionItems) {
  return (// eslint-disable-next-line no-use-extend-native/no-use-extend-native
    Object.fromEntries(subscriptionItems.map(function (key) {
      return [key, true];
    }))
  );
};

var defaultSubscription$1 = makeSubscriptionObject(formSubscriptionItems);

var useForm = function useForm(config) {
  var formApi = createForm({
    initialValues: config.initialValues,
    validate: Array.isArray(config.validate) ? composeFormValidators(config.validate) : config.validate,
    onSubmit: config.onSubmit
  });
  var finalForm = ref(Object.assign({}, formApi, {
    handleSubmit: function handleSubmit(event) {
      event && event.preventDefault();
      formApi.submit();
    }
  }));
  var formState = ref();
  var unsubscribe = finalForm.value.subscribe(function (state) {
    formState.value = state;
  }, config.subscription || defaultSubscription$1);
  onUnmounted(unsubscribe);
  provide('finalForm', finalForm);
  return {
    finalForm: finalForm,
    formState: formState,
    unsubscribe: unsubscribe
  };
};

var FinalForm = {
  name: 'final-form',
  props: {
    initialValues: Object,
    submit: {
      type: Function,
      "default": function _default() {}
    },
    subscription: Object,
    validate: [Function, Array]
  },
  setup: function setup(props) {
    var _useForm = useForm({
      initialValues: props.initialValues,
      validate: props.validate,
      subscription: props.subscription,
      onSubmit: props.submit
    }),
        finalForm = _useForm.finalForm,
        formState = _useForm.formState;

    return {
      finalForm: finalForm,
      formState: formState
    };
  },
  methods: {
    handleSubmit: function handleSubmit(event) {
      event && event.preventDefault();
      this.finalForm.submit();
    }
  },
  watch: {
    formState: function formState(state) {
      this.$emit('change', state);
    }
  },
  render: function render() {
    var children = this.$slots["default"] ? this.$slots["default"](Object.assign({}, this.formState, this.finalForm)) : this.$slots["default"];
    return h('div', null, getChildren(children));
  }
};

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var useCurrentForm = function useCurrentForm() {
  var finalForm = inject('finalForm');

  if (!finalForm) {
    throw new Error('Form was\'t found. Please provide it using `<FinalForm>` component or `useForm` hook.');
  }

  return finalForm;
};

var defaultSubscription = makeSubscriptionObject(fieldSubscriptionItems);

var useField = function useField(config) {
  var finalForm = config.finalForm || useCurrentForm();
  var fieldState = ref({});
  var unregister = finalForm.value.registerField(config.name, function (state) {
    fieldState.value = state;
  }, config.subscription || defaultSubscription, {
    getValidator: Array.isArray(config.validate) ? composeFieldValidators(config.validate) : function () {
      return config.validate;
    }
  });
  onUnmounted(unregister);
  return {
    fieldState: computed(function () {
      return Object.assign({}, fieldState.value, {
        events: {
          input: function input(event) {
            return fieldState.value.change(event.target.value);
          },
          focus: function focus() {
            return fieldState.value.focus();
          },
          blur: function blur() {
            return fieldState.value.blur();
          }
        }
      });
    }),
    unregister: unregister
  };
};

var _excluded = ["blur", "change", "focus", "value", "name"];
var FinalField = {
  name: 'final-field',
  props: {
    name: {
      required: true,
      type: String
    },
    validate: [Function, Array],
    subscription: Object
  },
  setup: function setup(props) {
    var _useField = useField({
      name: props.name,
      subscription: props.subscription,
      validate: props.validate
    }),
        finalForm = _useField.finalForm,
        fieldState = _useField.fieldState;

    return {
      finalForm: finalForm,
      fieldState: fieldState
    };
  },
  watch: {
    fieldState: function fieldState(state) {
      this.$emit('change', state);
    }
  },
  render: function render() {
    var _this$fieldState = this.fieldState;
        _this$fieldState.blur;
        var change = _this$fieldState.change;
        _this$fieldState.focus;
        var value = _this$fieldState.value,
        name = _this$fieldState.name,
        meta = _objectWithoutProperties(_this$fieldState, _excluded);

    var children = this.$slots["default"]({
      events: this.fieldState.events,
      change: change,
      value: value,
      name: name,
      meta: meta
    });
    return getChildren(children)[0];
  }
};

export { FinalField, FinalForm, useField, useForm };
